import { PropsWithChildren } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

import { Button, Dialog } from 'shared/components'
import { useTranslation } from 'libs/i18n'
import { cn } from 'utils/style'

type ConfirmModalProps = PropsWithChildren<{
	isOpened: boolean
	title: string
	description?: string
	confirmText?: string
	tone?: 'primary' | 'danger'
	isLoading?: boolean
	disabled?: boolean
	closeModal: () => void
	confirm: () => void
}>

const cancelButtonClassName =
	'h-9 rounded-md border-0 px-4 text-sm font-medium text-foreground shadow-none hover:bg-muted'
const confirmButtonClassName =
	'h-9 rounded-md px-4 text-sm font-medium shadow transition-colors hover:bg-primary/90'

export const ConfirmModal = ({
	isOpened,
	title,
	description,
	confirmText,
	tone = 'primary',
	isLoading,
	disabled,
	closeModal,
	confirm,
	children
}: ConfirmModalProps) => {
	const { t } = useTranslation()

	return (
		<Dialog isOpened={isOpened} ariaLabel={title} className="max-w-md" closeModal={closeModal}>
			<div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5 pt-6">
				<h2 className="pr-8 font-display text-lg font-bold tracking-tight text-foreground">
					{title}
				</h2>
				{description && (
					<p className="mt-1.5 text-[13px] leading-5 text-muted-foreground">{description}</p>
				)}
				{children && (
					<div
						className={cn(
							'text-[13px] leading-5 text-muted-foreground',
							description ? 'mt-4' : 'mt-1.5'
						)}>
						{children}
					</div>
				)}
			</div>
			<button
				type="button"
				aria-label={t('Close')}
				className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
				onClick={closeModal}>
				<XMarkIcon className="h-4 w-4 stroke-2" />
			</button>
			<div className="flex items-center justify-end gap-2 border-t border-border bg-background/60 px-6 py-4">
				<Button styleType="outline" className={cancelButtonClassName} onClick={closeModal}>
					{t('Cancel')}
				</Button>
				<Button
					className={cn(
						confirmButtonClassName,
						tone === 'danger' && 'bg-danger hover:bg-danger/90'
					)}
					isLoading={isLoading}
					disabled={disabled}
					onClick={confirm}>
					{confirmText ?? t('Confirm')}
				</Button>
			</div>
		</Dialog>
	)
}
